// WP-0.7 (§18.1): light/dark themes from the same tokens. `data-theme` on <html> is
// ALWAYS the resolved appearance ('light' | 'dark'), never the raw 'system' choice —
// CSS keys off that one attribute exclusively (no parallel `@media (prefers-color-scheme)`
// block). A raw live media-query cascade doesn't reliably repaint already-painted elements
// when only the OS/emulated preference changes with no accompanying DOM mutation (observed
// directly: form inputs kept their stale background after a live scheme switch even though
// getComputedStyle already reported the new value). Routing every change through a real
// attribute write sidesteps that: attribute-selector changes are a first-class case every
// CSS engine invalidates and repaints correctly. A blocking inline script in index.html
// sets the attribute before first paint so there is no flash of the wrong theme.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'pfos-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  // Keep native widgets (scrollbars, autofill, date pickers) in step too.
  document.documentElement.style.colorScheme = resolved;
}

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* SSR/tests without storage — fall through */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function ThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Track OS preference so a 'system' choice re-renders when the OS flips.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent): void => {
      setSystemDark(e.matches);
    };
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
